/**
 * DamyxCodeWorkspace - Entorno de Ejecución de Código
 * 
 * Este componente proporciona un entorno de ejecución completo para código
 * generado por DamyxIA. Soporta:
 * - JavaScript (sandbox seguro)
 * - React/JSX (renderizado en iframe)
 * - HTML/CSS (preview en iframe)
 * - Ejecución automática de código
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Play, Square, RefreshCw, Code, Eye, Terminal, 
  Copy, Check, Download, Maximize2, Minimize2, X,
  FileCode, Loader2, AlertCircle, CheckCircle2,
  Zap, Layout, Monitor, FolderOpen, FileCode2
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import SaveToGitHubButton from './SaveToGitHubButton';
import CodePanel from './CodePanel';
import './AltModePanel.css';

// 🟥 CUADRO NEGRO — MODO ALTERNATIVO DE PREVIEW
// Detecta errores de runtime cuyo origen es un proyecto NO ejecutable en
// el sandbox web (React Native, APIs nativas, scripts CLI, backend sin UI,
// código sin entry-point web, etc.). En esos casos el Workspace debe
// mostrar un panel comunicativo "Trabajo completado" en vez del error
// técnico rojo.
//
// ÁMBITO: UI del Workspace ÚNICAMENTE. NO afecta al orquestador, al motor
// de planes, a la lógica de DamyxIA ni al Auto-Builder. El código se sigue
// generando y almacenando con normalidad.
const NON_EXECUTABLE_ERROR_PATTERNS = [
  // React Native primitives
  /\bView is not defined\b/i,
  /\bText is not defined\b/i,
  /\bImage is not defined\b/i,
  /\bStyleSheet is not defined\b/i,
  /\bScrollView is not defined\b/i,
  /\bTouchableOpacity is not defined\b/i,
  /\bSafeAreaView is not defined\b/i,
  /\bFlatList is not defined\b/i,
  // 🟥 P24.1 · React Native primitives que EXISTEN en el sandbox como
  // objetos globales (por alguna dependencia CSS-in-JS o MUI) pero
  // NO tienen los métodos de RN. Bug reportado en prod:
  // "StyleSheet.create is not a function" con app móvil de foto→PDF.
  /\bStyleSheet\.create is not a function\b/i,
  /\bStyleSheet\.[a-zA-Z_]+ is not a function\b/i,
  /\b(View|Text|ScrollView|TouchableOpacity|SafeAreaView|FlatList|Image) is not a function\b/i,
  /\bAlert\.alert is not a function\b/i,
  /\bDimensions\.get is not a function\b/i,
  /\bPlatform\.(OS|select) is not (a function|defined)\b/i,
  /\bLinking\.[a-zA-Z_]+ is not a function\b/i,
  /\bAsyncStorage\.[a-zA-Z_]+ is not a function\b/i,
  // 🟥 P24.2 · React Navigation (RN Navigation library) — reportado
  // por owner: "createStackNavigator is not defined".
  /\bcreateStackNavigator\b/i,
  /\bcreateBottomTabNavigator\b/i,
  /\bcreateDrawerNavigator\b/i,
  /\bcreateMaterialTopTabNavigator\b/i,
  /\bcreateNativeStackNavigator\b/i,
  /\bNavigationContainer is not defined\b/i,
  /@react-navigation/i,
  // 🟥 P24.2 · react-beautiful-dnd — librería externa que no está en el
  // iframe. Reportado por owner: "DragDropContext is not defined".
  // Nota: es lib React web (no RN) pero no está cargada en el sandbox.
  // Mientras no la incluyamos, mostramos el panel Cuadro Negro
  // categoría 'no-ui' con instrucción de descargar.
  /\bDragDropContext is not defined\b/i,
  /\b(Droppable|Draggable) is not defined\b/i,
  /\breact-beautiful-dnd\b/i,
  /\b@hello-pangea\/dnd\b/i,
  /\b@dnd-kit\//i,
  /\bRNCamera\b/i,
  /react-native/i,
  /\bexpo[/-]/i,
  // APIs nativas / no-web
  /\bnavigator\.camera\b/i,
  /\bRNFS\b/i,
  /\bnative module\b/i,
  // Entorno no-web
  /\bdocument is not defined\b/i,
  /\bwindow is not defined\b/i,
  /\bprocess is not defined\b/i,
  /\brequire is not defined\b/i,
  // Sin punto de entrada
  /\bMainComponent is not defined\b/i,
  /\bdefault export\b.*\bnot found\b/i,
  // Marcador interno del Detector Pre-Render (CUADRO NEGRO).
  // Cuando `detectNonExecutableCode()` clasifica el codigo como
  // no-ejecutable, seteamos error=`__NON_EXECUTABLE__:<cat>` para que
  // el panel del Modo Alternativo se monte sin pasar por el iframe.
  /^__NON_EXECUTABLE__:/,
];

const detectNonExecutableError = (errorMsg) => {
  if (!errorMsg || typeof errorMsg !== 'string') return false;
  return NON_EXECUTABLE_ERROR_PATTERNS.some((re) => re.test(errorMsg));
};

// 🟥 CUADRO NEGRO — MENSAJES ESPECÍFICOS POR CATEGORÍA.
// Mapping bilingue (la app soporta ES/EN). Si la categoria es null →
// el panel usa el subtitulo generico (mensaje original "Este proyecto
// no puede ejecutarse..."). Si la categoria existe → el panel muestra
// el mensaje contextual de su categoria. Esto refuerza la sensacion de
// que DamyxIA entiende QUE clase de proyecto genero.
const ALT_MESSAGES = {
  es: {
    'react-native':
      'Este proyecto está diseñado para dispositivos móviles (iOS/Android) y utiliza componentes nativos. No puede previsualizarse en el Workspace web. Descárgalo para ejecutarlo con Expo Go, Android Studio o Xcode.',
    'backend':
      'Este proyecto es un servidor backend (Express/Fastify/Koa). No tiene interfaz visual y no puede renderizarse en el Workspace. Descárgalo para ejecutarlo con Node.js en tu entorno local.',
    'cli':
      'Este proyecto es una herramienta de línea de comandos (CLI). No tiene interfaz visual y no puede previsualizarse en el Workspace. Descárgalo para ejecutarlo desde la terminal.',
    'no-ui':
      'Este proyecto no contiene una interfaz visual compatible con el Workspace. Descárgalo para continuar tu desarrollo en tu entorno preferido.',
  },
  en: {
    'react-native':
      'This project is designed for mobile devices (iOS/Android) and uses native components. It cannot be previewed in the web Workspace. Download it to run it with Expo Go, Android Studio, or Xcode.',
    'backend':
      'This project is a backend server (Express/Fastify/Koa). It has no visual interface and cannot be rendered in the Workspace. Download it to run it with Node.js in your local environment.',
    'cli':
      'This project is a command-line tool (CLI). It has no visual interface and cannot be previewed in the Workspace. Download it to run it from your terminal.',
    'no-ui':
      'This project does not contain a visual interface compatible with the Workspace. Download it to continue development in your preferred environment.',
  },
};

// Extrae la categoria desde el `error` actual. Dos fuentes:
//  - Pre-render: error con prefijo `__NON_EXECUTABLE__:<cat>` (claro).
//  - Post-render: error de runtime (View/document/etc.) → mapeamos por
//    heuristica para inferir la categoria probable.
// Si no hay match → null (el panel usa subtitulo generico).
const extractAltCategory = (errorMsg) => {
  if (!errorMsg || typeof errorMsg !== 'string') return null;
  // Pre-render marker.
  const preMatch = errorMsg.match(/^__NON_EXECUTABLE__:([\w-]+)/);
  if (preMatch) return preMatch[1];
  // Post-render heuristica.
  if (/\b(View|Text|Image|StyleSheet|ScrollView|TouchableOpacity|SafeAreaView|FlatList) is not defined\b/i.test(errorMsg)) return 'react-native';
  // 🟥 P24.1 · variantes "is not a function" para primitives RN que
  // aparecen como objetos globales pero sin métodos válidos.
  if (/\bStyleSheet\.[a-zA-Z_]+ is not a function\b/i.test(errorMsg)) return 'react-native';
  if (/\b(View|Text|ScrollView|TouchableOpacity|SafeAreaView|FlatList|Image) is not a function\b/i.test(errorMsg)) return 'react-native';
  if (/\b(Alert\.alert|Dimensions\.get|Platform\.(OS|select)|Linking\.[a-zA-Z_]+|AsyncStorage\.[a-zA-Z_]+) is not (a function|defined)\b/i.test(errorMsg)) return 'react-native';
  // 🟥 P24.2 · React Navigation → react-native
  if (/\bcreate(Stack|BottomTab|Drawer|MaterialTopTab|NativeStack)Navigator\b/i.test(errorMsg)) return 'react-native';
  if (/\bNavigationContainer is not defined\b|@react-navigation/i.test(errorMsg)) return 'react-native';
  if (/\bRNCamera\b|react-native|\bexpo[/-]|\bRNFS\b|\bnative module\b|\bnavigator\.camera\b/i.test(errorMsg)) return 'react-native';
  // 🟥 P24.2 · react-beautiful-dnd / dnd-kit → no-ui (lib externa no
  // disponible en sandbox). Mensaje "Este proyecto no contiene una
  // interfaz visual compatible con el Workspace. Descárgalo..."
  if (/\bDragDropContext is not defined\b|\b(Droppable|Draggable) is not defined\b/i.test(errorMsg)) return 'no-ui';
  if (/\breact-beautiful-dnd\b|@hello-pangea\/dnd|@dnd-kit\//i.test(errorMsg)) return 'no-ui';
  if (/\bdocument is not defined\b|\bwindow is not defined\b/i.test(errorMsg)) return 'no-ui';
  if (/\bprocess is not defined\b|\brequire is not defined\b/i.test(errorMsg)) return 'no-ui';
  if (/\bMainComponent is not defined\b|\bdefault export\b.*\bnot found\b/i.test(errorMsg)) return 'no-ui';
  return null;
};

// 🟥 CUADRO NEGRO — DETECTOR DE TIPO DE PROYECTO (PRE-RENDER).
// Analiza el CÓDIGO ANTES de mandarlo al iframe sandbox. Si detecta que
// el proyecto no es ejecutable en web (React Native, backend, CLI, sin
// UI) activamos el Modo Alternativo SIN intentar renderizar — evita
// pantalla en blanco, parpadeos y errores intermitentes.
//
// Devuelve: null si el código parece ejecutable web, o un string-key
// describiendo la categoría detectada (para futuras estadísticas /
// mensajes específicos). El consumidor solo necesita `!!result` para
// disparar el Modo Alternativo.
//
// Diseño defensivo: si el detector duda (heurística débil), retorna
// null para NO bloquear código web válido. Mejor un error post-render
// que un falso positivo que oculta una app real.
const detectNonExecutableCode = (code) => {
  if (!code || typeof code !== 'string') return null;
  // Quitamos comentarios para evitar falsos positivos por strings en
  // docstrings o comentarios. No es un parser, pero alcanza para la
  // mayoria de los casos:  // ... \n  o /* ... */
  const stripped = code
    .replace(/\/\/[^\n]*/g, '')
    .replace(/\/\*[\s\S]*?\*\//g, '');

  // A) React Native / Móvil — imports o componentes.
  if (/from\s+['"](?:react-native|expo|expo-[a-z-]+|@react-native[/-][a-z-]+|react-native-[a-z-]+)['"]/i.test(stripped)) {
    return 'react-native';
  }
  if (/\bimport\s+[^;]*\bfrom\s+['"](?:react-native|expo)['"]/i.test(stripped)) {
    return 'react-native';
  }
  // 🟥 P24.1 · React Native SIN import explícito. El LLM a veces omite
  // los imports pero deja API RN visible ("StyleSheet.create({..})",
  // "<View>", "<Text>", "<SafeAreaView>", "Alert.alert(...)", etc.).
  // Esto disparaba el error rojo "StyleSheet.create is not a function"
  // en el iframe. Detectarlo antes evita el flash de error.
  if (/\bStyleSheet\.(create|flatten|compose|hairlineWidth)\b/.test(stripped)) {
    return 'react-native';
  }
  if (/<\s*(View|SafeAreaView|ScrollView|TouchableOpacity|TouchableHighlight|FlatList|SectionList|Modal|RefreshControl|StatusBar|KeyboardAvoidingView)\b/.test(stripped)) {
    return 'react-native';
  }
  if (/\bAlert\s*\.\s*alert\s*\(/.test(stripped)) {
    return 'react-native';
  }
  if (/\bDimensions\s*\.\s*get\s*\(\s*['"]window['"]\s*\)/.test(stripped)) {
    return 'react-native';
  }
  if (/\bPlatform\s*\.\s*(OS|select)\b/.test(stripped)) {
    return 'react-native';
  }
  if (/\bAsyncStorage\s*\.\s*(getItem|setItem|removeItem|clear|mergeItem)\b/.test(stripped)) {
    return 'react-native';
  }
  if (/\bLinking\s*\.\s*(openURL|canOpenURL|getInitialURL)\b/.test(stripped)) {
    return 'react-native';
  }
  // 🟥 P24.2 · React Navigation — cualquier import de @react-navigation/*
  // o uso de createXxxNavigator() significa proyecto RN.
  if (/from\s+['"]@react-navigation\/[a-z-]+['"]/i.test(stripped)) {
    return 'react-native';
  }
  if (/\bcreate(Stack|BottomTab|Drawer|MaterialTopTab|NativeStack)Navigator\s*\(/.test(stripped)) {
    return 'react-native';
  }
  if (/<\s*NavigationContainer\b/.test(stripped)) {
    return 'react-native';
  }

  // A.2) Librerías externas web que NO están en el sandbox del iframe.
  // Detectarlas antes evita el error rojo genérico "X is not defined".
  // Se clasifican como 'no-ui' (mensaje: "descárgalo para tu entorno").
  if (/from\s+['"](?:react-beautiful-dnd|@hello-pangea\/dnd|@dnd-kit\/[a-z-]+)['"]/i.test(stripped)) {
    return 'no-ui';
  }
  if (/<\s*(DragDropContext|Droppable|Draggable)\b/.test(stripped) &&
      /\b(onDragEnd|draggableId|droppableId)\b/.test(stripped)) {
    return 'no-ui';
  }

  // B) Backend / Servidor — imports + listen().
  if (/from\s+['"](?:express|fastify|koa|hono|@hapi\/[a-z-]+)['"]/i.test(stripped)) {
    return 'backend';
  }
  if (/require\s*\(\s*['"](?:express|fastify|koa|hono|http|fs|path)['"]\s*\)/i.test(stripped)) {
    return 'backend';
  }
  if (/\bapp\s*\.\s*listen\s*\(/i.test(stripped) ||
      /\bserver\s*\.\s*listen\s*\(/i.test(stripped) ||
      /\brouter\s*\.\s*(get|post|put|delete|patch)\s*\(/i.test(stripped)) {
    return 'backend';
  }

  // C) Scripts CLI — process.argv, readline, child_process.
  if (/\bprocess\s*\.\s*argv\b/.test(stripped)) {
    return 'cli';
  }
  if (/require\s*\(\s*['"](?:readline|child_process|os|cluster)['"]\s*\)/.test(stripped)) {
    return 'cli';
  }
  if (/from\s+['"](?:readline|child_process|os|cluster)['"]/.test(stripped)) {
    return 'cli';
  }

  // D) Sin UI Web — código relevante pero sin JSX ni render entry-point.
  // Solo aplica si el código es razonablemente largo (≥30 chars no-whitespace)
  // y NO tiene NINGUN indicio de JSX/render. Evita falsos positivos en
  // snippets cortos.
  const trimmedNoWs = stripped.replace(/\s+/g, ' ').trim();
  if (trimmedNoWs.length >= 30) {
    const hasJSX = /<\s*[A-Za-z][A-Za-z0-9]*\b[^>]*>/.test(stripped) ||
                   /\bReact\s*\.\s*createElement\s*\(/.test(stripped);
    const hasExportDefault = /\bexport\s+default\b/.test(stripped);
    const hasMainComponent = /\b(?:function|const|class|let|var)\s+(?:MainComponent|App)\b/.test(stripped);
    if (!hasJSX && !hasExportDefault && !hasMainComponent) {
      return 'no-ui';
    }
  }

  return null;
};

// Colores del sistema
const COLORS = {
  background: '#0F172A',
  panel: '#1E293B',
  border: '#334155',
  textPrimary: '#FFFFFF',
  textSecondary: '#CBD5E1',
  success: '#22C55E',
  error: '#EF4444',
  warning: '#F59E0B',
  info: '#3B82F6',
};

// =====================================================
// EJECUTOR DE JAVASCRIPT EN SANDBOX
// =====================================================
export const JavaScriptRunner = ({ code, autoRun = true, onResult }) => {
  const { language } = useLanguage();
  const [output, setOutput] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const outputRef = useRef(null);

  const executeCode = async () => {
    setIsRunning(true);
    setOutput([]);

    try {
      const logs = [];
      const sandboxConsole = {
        log: (...args) => logs.push({ type: 'log', content: args.map(formatArg).join(' ') }),
        info: (...args) => logs.push({ type: 'info', content: args.map(formatArg).join(' ') }),
        warn: (...args) => logs.push({ type: 'warn', content: args.map(formatArg).join(' ') }),
        error: (...args) => logs.push({ type: 'error', content: args.map(formatArg).join(' ') }),
        table: (data) => logs.push({ type: 'table', content: JSON.stringify(data, null, 2) }),
      };

      const AsyncFunction = Object.getPrototypeOf(async function(){}).constructor;
      const userFunction = new AsyncFunction(
        'console', 'Math', 'Date', 'JSON', 'Array', 'Object', 'String', 'Number', 'Promise',
        `try { ${code} } catch (e) { console.error('Error:', e.message); throw e; }`
      );

      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout: 10s')), 10000)
      );

      const result = await Promise.race([
        userFunction(sandboxConsole, Math, Date, JSON, Array, Object, String, Number, Promise),
        timeoutPromise
      ]);

      if (result !== undefined) {
        logs.push({ type: 'result', content: formatArg(result) });
      }

      setOutput(logs);
      if (onResult) onResult({ success: true, logs, result });
    } catch (err) {
      setOutput([{ type: 'error', content: err.message }]);
      if (onResult) onResult({ success: false, error: err.message });
    } finally {
      setIsRunning(false);
      setHasRun(true);
    }
  };

  const formatArg = (arg) => {
    if (arg === null) return 'null';
    if (arg === undefined) return 'undefined';
    if (typeof arg === 'object') {
      try { return JSON.stringify(arg, null, 2); } catch { return String(arg); }
    }
    return String(arg);
  };

  useEffect(() => {
    if (autoRun && code && !hasRun) {
      const timer = setTimeout(executeCode, 300);
      return () => clearTimeout(timer);
    }
  }, [autoRun, code, hasRun]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  const getOutputColor = (type) => {
    switch (type) {
      case 'error': return 'text-red-400';
      case 'warn': return 'text-yellow-400';
      case 'info': return 'text-blue-400';
      case 'result': return 'text-emerald-400';
      default: return 'text-zinc-300';
    }
  };

  return (
    <div className="rounded-xl overflow-hidden border" style={{ backgroundColor: COLORS.panel, borderColor: COLORS.border }}>
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: COLORS.border }}>
        <div className="flex items-center gap-2">
          <Terminal className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-medium text-white">
            {language === 'es' ? 'Consola JavaScript' : 'JavaScript Console'}
          </span>
        </div>
        <button
          onClick={executeCode}
          disabled={isRunning}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium ${
            isRunning ? 'bg-zinc-700 text-zinc-400' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
          }`}
        >
          {isRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
          {isRunning ? (language === 'es' ? 'Ejecutando...' : 'Running...') : (language === 'es' ? 'Ejecutar' : 'Run')}
        </button>
      </div>
      
      <div className="p-3 border-b bg-zinc-900/50" style={{ borderColor: COLORS.border }}>
        <pre className="text-xs font-mono text-zinc-400 max-h-32 overflow-auto"><code>{code}</code></pre>
      </div>
      
      <div ref={outputRef} className="p-3 min-h-[60px] max-h-40 overflow-y-auto bg-zinc-900/80">
        {output.length === 0 && !isRunning && (
          <span className="text-xs text-zinc-500 italic">
            {hasRun ? (language === 'es' ? 'Sin salida' : 'No output') : (language === 'es' ? 'Esperando...' : 'Waiting...')}
          </span>
        )}
        {isRunning && (
          <div className="flex items-center gap-2 text-zinc-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-xs">{language === 'es' ? 'Ejecutando...' : 'Running...'}</span>
          </div>
        )}
        {output.map((log, idx) => (
          <div key={idx} className={`text-xs font-mono ${getOutputColor(log.type)}`}>
            <pre className="whitespace-pre-wrap break-all">{log.content}</pre>
          </div>
        ))}
      </div>
    </div>
  );
};

// =====================================================
// PREVIEW DE REACT/JSX EN IFRAME — ESTABLE (sin reload)
// =====================================================
export const ReactPreview = ({ code, autoRun = true, title, onDownloadProject, onShowFiles, projectFiles = [], conversationId = null }) => {
  const { language } = useLanguage();
  const iframeRef = useRef(null);
  const iframeReady = useRef(false);
  const pendingCode = useRef(null);
  const lastSentCode = useRef('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // 🟪 P24.9-CODE · toggle del panel Códigos/Code (overlay sobre el iframe).
  const [showCodePanel, setShowCodePanel] = useState(false);

  // 🟪 Archivos que verá el panel Códigos. Si el proyecto activo trae
  // varios archivos (multi-file) los usamos tal cual; si solo hay `code`
  // monolítico (single-file), sintetizamos un archivo virtual `App.jsx`
  // para que el visor siempre muestre algo cuando hay proyecto abierto.
  const codePanelFiles = useMemo(() => {
    if (Array.isArray(projectFiles) && projectFiles.length > 0) {
      return projectFiles;
    }
    if (typeof code === 'string' && code.trim().length > 0) {
      return [{ path: 'App.jsx', content: code, language: 'jsx' }];
    }
    return [];
  }, [projectFiles, code]);

  const cleanCode = useMemo(() => {
    if (!code) return '';

    // Helper: split a JSX-style import name list ("X, Y as Z, W") into pairs
    const splitImports = (raw) => raw.split(',').map(s => s.trim()).filter(Boolean).map(item => {
      const parts = item.split(/\s+as\s+/);
      return { original: parts[0].trim(), alias: (parts[1] || parts[0]).trim() };
    });

    let cleaned = code;

    // Step 1: collect ALL imports from recharts + lucide-react FIRST (single pass).
    // Recharts always wins on conflicts (charts > icons). Duplicate aliases from
    // different sources would otherwise produce `var X = ...; var X = ...;`
    // which is a SyntaxError in strict-mode JS and silently breaks the iframe.
    const declared = new Set();
    const decls = [];

    cleaned = cleaned.replace(/import\s*\{([^}]+)\}\s*from\s*['"]recharts['"];?\s*/g, (_, names) => {
      splitImports(names).forEach(({ original, alias }) => {
        if (declared.has(alias)) return;
        declared.add(alias);
        decls.push(`${alias} = (window.Recharts && window.Recharts.${original}) || window.__NoopComp__`);
      });
      return '';
    });

    cleaned = cleaned.replace(/import\s*\{([^}]+)\}\s*from\s*['"]lucide-react['"];?\s*/g, (_, names) => {
      splitImports(names).forEach(({ alias }) => {
        if (declared.has(alias)) return;
        declared.add(alias);
        decls.push(`${alias} = window.__LucideShim__`);
      });
      return '';
    });

    // Strip remaining imports / exports
    cleaned = cleaned
      .replace(/import\s+.*?from\s+['"][^'"]+['"];?\s*/g, '')
      .replace(/import\s+['"][^'"]+['"];?\s*/g, '')
      .replace(/export\s+default\s+/g, '')
      .replace(/export\s+/g, '')
      .replace(/React\.(useState|useEffect|useRef|useCallback|useMemo|useContext|useReducer)/g, '$1');

    // Auto-fix Recharts <ResponsiveContainer>...</ResponsiveContainer> wrappers.
    // Recharts measures the parent and renders to 0px when the parent has no
    // fixed height (typical inside a sandboxed srcdoc iframe). We rewrite
    // the wrapper to a plain div with explicit dimensions AND inject
    // numeric width/height props into the chart child.
    var CHART_TAGS = ['BarChart','LineChart','PieChart','AreaChart','RadarChart','ComposedChart','ScatterChart','RadialBarChart','FunnelChart','SankeyChart','Treemap'];
    var chartTagsAlt = CHART_TAGS.join('|');
    var rcTag = new RegExp('<ResponsiveContainer\\b[^>]*>\\s*<(' + chartTagsAlt + ')\\b([^>]*?)(/?)>', 'g');
    cleaned = cleaned.replace(rcTag, function(match, tag, attrs, selfClose) {
      // Strip any existing width/height from chart attrs to avoid duplicate props
      var cleanAttrs = (attrs || '')
        .replace(/\bwidth\s*=\s*(?:["'][^"']*["']|\{[^}]*\})/g, '')
        .replace(/\bheight\s*=\s*(?:["'][^"']*["']|\{[^}]*\})/g, '');
      return '<div style={{width:"100%",minHeight:320}}><' + tag + cleanAttrs + ' width={520} height={300}' + selfClose + '>';
    });
    // Close the wrapper div whenever we see </ResponsiveContainer>
    cleaned = cleaned.replace(/<\/ResponsiveContainer>/g, '</div>');

    // Auto-fix: <ResponsiveContainer height="100%"> inside a chart that is
    // self-contained (kept as defensive fallback when chart inside is unknown).
    cleaned = cleaned
      .replace(/<ResponsiveContainer([^>]*?)height\s*=\s*["']100%["']([^>]*)>/g, '<ResponsiveContainer$1height={320}$2>')
      .replace(/<ResponsiveContainer([^>]*?)height\s*=\s*\{["']100%["']\}([^>]*)>/g, '<ResponsiveContainer$1height={320}$2>');

    // Prepend the deduplicated declarations
    const preamble = decls.length ? `var ${decls.join(', ')};\n` : '';
    return (preamble + cleaned).trim();
  }, [code]);

  // Template base estable — se crea UNA SOLA VEZ
  const stableTemplate = useMemo(() => {
    const vendorBase = window.location.origin + '/vendor';
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <script src="${vendorBase}/tailwindcss.js" onerror="var s=document.createElement('script');s.src='https://cdn.tailwindcss.com';s.onerror=function(){window.__cdnFail=true};document.head.appendChild(s)"><\/script>
  <script src="${vendorBase}/react.production.min.js" onerror="var s=document.createElement('script');s.src='https://unpkg.com/react@18/umd/react.production.min.js';s.onerror=function(){window.__cdnFail=true};document.head.appendChild(s)"><\/script>
  <script src="${vendorBase}/react-dom.production.min.js" onerror="var s=document.createElement('script');s.src='https://unpkg.com/react-dom@18/umd/react-dom.production.min.js';s.onerror=function(){window.__cdnFail=true};document.head.appendChild(s)"><\/script>
  <script src="${vendorBase}/babel.min.js" onerror="var s=document.createElement('script');s.src='https://unpkg.com/@babel/standalone@7/babel.min.js';s.onerror=function(){window.__cdnFail=true};document.head.appendChild(s)"><\/script>
  <script src="https://unpkg.com/prop-types@15.8.1/prop-types.min.js" onerror="window.__chartsUnavailable=true"><\/script>
  <script src="https://unpkg.com/recharts@2.12.7/umd/Recharts.js" onerror="window.__chartsUnavailable=true"><\/script>
  <style>
    html, body, #root { height: 100%; min-height: 100%; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: system-ui, -apple-system, sans-serif; background: #0f172a; color: white; min-height: 100vh; }
    #root { min-height: 100vh; padding: 16px; }
    /* Force Recharts to always have measurable space, regardless of how the
       LLM emitted its containers. Without this, ResponsiveContainer at 100%
       inside a flex parent often measures 0px and renders nothing. */
    .recharts-responsive-container { min-height: 320px !important; height: auto !important; width: 100% !important; }
    .recharts-wrapper { min-height: 280px; }
    .error-display { background: #7F1D1D; color: #FCA5A5; padding: 16px; border-radius: 8px; margin: 16px; font-family: monospace; font-size: 12px; white-space: pre-wrap; max-height: 300px; overflow: auto; }
    .loading-msg { color: #818cf8; font-size: 14px; display: flex; align-items: center; justify-content: center; height: 100vh; flex-direction: column; gap: 12px; }
    .loading-spinner { width: 32px; height: 32px; border: 3px solid #4f46e5; border-top-color: transparent; border-radius: 50%; animation: spin 1s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }
  </style>
</head>
<body>
  <div id="root"><div class="loading-msg"><div class="loading-spinner"></div><span>${language === 'es' ? 'Iniciando workspace...' : 'Starting workspace...'}</span></div></div>
  
  <script>
    // === MOTOR DE RENDER ESTABLE ===
    // El iframe se crea UNA VEZ. Las actualizaciones llegan via postMessage.
    var __reactRoot = null;
    var __ready = false;

    // Surface ALL silent failures: any uncaught error or unhandled rejection
    // paints the message into #root. Without this, a SyntaxError in the
    // user-code Function constructor produces an empty black iframe.
    // We use capture=true so it fires before React swallows error events.
    window.addEventListener('error', function(e) {
      try {
        var msg = (e && (e.message || e.error && e.error.message)) || 'Unknown error';
        var root = document.getElementById('root');
        if (root) {
          root.innerHTML = '<div class="error-display"><strong>Render error:</strong>\\n\\n' + msg + '</div>';
        }
        if (window.parent) window.parent.postMessage({ type: 'RENDER_ERROR', error: msg }, '*');
      } catch (_) {}
    }, true);
    window.addEventListener('unhandledrejection', function(e) {
      try {
        var msg = (e && e.reason && (e.reason.message || String(e.reason))) || 'Unhandled rejection';
        var root = document.getElementById('root');
        if (root) {
          root.innerHTML = '<div class="error-display"><strong>Promise error:</strong>\\n\\n' + msg + '</div>';
        }
      } catch (_) {}
    });

    // Componente no-op para Recharts faltantes (evita que un nombre desconocido rompa el render)
    window.__NoopComp__ = function() { return null; };

    // Defense-in-depth: el LLM a veces ignora la instruccion de no usar
    // <ResponsiveContainer>. Esa lib mide el padre y si es 0px renderiza null.
    // Sobrescribimos ResponsiveContainer con un wrapper que SIEMPRE pasa width
    // y height numericos al chart hijo, garantizando que siempre se pinte.
    function patchResponsiveContainer() {
      if (!window.Recharts || !window.React) return;
      var React = window.React;
      window.Recharts.ResponsiveContainer = function(props) {
        var w = (typeof props.width === 'number' && props.width > 0) ? props.width : 520;
        var h = (typeof props.height === 'number' && props.height > 0) ? props.height : 300;
        try {
          var child = React.Children.only(props.children);
          return React.createElement('div', { style: { width: w, height: h, position: 'relative' } },
            React.cloneElement(child, { width: w, height: h })
          );
        } catch (e) {
          return React.createElement('div', { style: { width: w, height: h } }, props.children || null);
        }
      };
    }

    // Shim universal de iconos lucide-react: cualquier nombre importado renderiza
    // un SVG genérico de 16x16 con el nombre como aria-label. Asi los dashboards
    // siguen renderizando aun sin la libreria real.
    window.__LucideShim__ = function(props) {
      var p = props || {};
      var size = p.size || 16;
      var cls = p.className || 'inline-block align-middle';
      return React.createElement('svg', {
        width: size, height: size, viewBox: '0 0 24 24',
        fill: 'none', stroke: 'currentColor', strokeWidth: 2,
        strokeLinecap: 'round', strokeLinejoin: 'round',
        className: cls, 'aria-hidden': 'true'
      }, React.createElement('circle', { cx: 12, cy: 12, r: 9 }));
    };

    function waitForLibraries(callback) {
      var attempts = 0;
      (function check() {
        attempts++;
        if (typeof React !== 'undefined' && typeof ReactDOM !== 'undefined' && typeof Babel !== 'undefined') {
          // Patch Recharts.ResponsiveContainer right after libraries are ready,
          // before any user code executes.
          patchResponsiveContainer();
          callback();
        } else if (window.__cdnFail) {
          document.getElementById('root').innerHTML = '<div class="error-display"><strong>${language === 'es' ? 'Error de red:' : 'Network error:'}</strong> ${language === 'es' ? 'No se pudieron cargar las bibliotecas.' : 'Libraries could not be loaded.'}</div>';
        } else if (attempts < 100) {
          setTimeout(check, 100);
        } else {
          document.getElementById('root').innerHTML = '<div class="error-display">${language === 'es' ? 'Error: Las bibliotecas no se cargaron. Verifica tu conexión.' : 'Error: Libraries did not load. Check your connection.'}</div>';
        }
      })();
    }

    function renderCode(userCode) {
      if (!userCode || !userCode.trim()) return;

      var useState = React.useState;
      var useEffect = React.useEffect;
      var useRef = React.useRef;
      var useCallback = React.useCallback;
      var useMemo = React.useMemo;
      var useContext = React.useContext;
      var useReducer = React.useReducer;
      var Fragment = React.Fragment;
      var createContext = React.createContext;

      // Error boundary to catch async render errors (React 18 commit phase)
      // that escape the synchronous try/catch below.
      function makeBoundary() {
        function Boundary(props) {
          var s = React.useState(null);
          var error = s[0];
          var setError = s[1];
          // Sync mount: store setError on a ref the parent can use
          Boundary.__setErr = setError;
          if (error) {
            return React.createElement('div', { className: 'error-display' },
              React.createElement('strong', null, 'React render error: '),
              React.createElement('br', null),
              String(error.message || error)
            );
          }
          return props.children;
        }
        // Class boundary for componentDidCatch
        function ClassBoundary(props) { return Boundary(props); }
        // Use a real class for componentDidCatch hook
        var EB = (function(){
          function E(p){ React.Component.call(this, p); this.state = { error: null }; }
          E.prototype = Object.create(React.Component.prototype);
          E.prototype.constructor = E;
          E.getDerivedStateFromError = function(error) { return { error: error }; };
          E.prototype.componentDidCatch = function(error){
            window.parent.postMessage({ type: 'RENDER_ERROR', error: 'Render: ' + (error && error.message || String(error)) }, '*');
          };
          E.prototype.render = function() {
            if (this.state.error) {
              return React.createElement('div', { className: 'error-display' },
                React.createElement('strong', null, 'React render error:'),
                React.createElement('pre', { style: { whiteSpace: 'pre-wrap', marginTop: 8 } }, String(this.state.error.message || this.state.error))
              );
            }
            return this.props.children;
          };
          return E;
        })();
        return EB;
      }
      var ErrorBoundary = makeBoundary();

      try {
        var transpiledCode;
        try {
          transpiledCode = Babel.transform(userCode, {
            presets: ['react'],
            filename: 'component.jsx'
          }).code;
        } catch (babelErr) {
          // Babel compile error: surface to UI immediately so it isn't silent.
          document.getElementById('root').innerHTML = '<div class="error-display"><strong>Babel compile error:</strong>\\n\\n' + babelErr.message + '</div>';
          window.parent.postMessage({ type: 'RENDER_ERROR', error: 'Babel: ' + babelErr.message }, '*');
          return;
        }

        // Detectar nombres de componentes
        var definedNames = [];
        var defRegex = /(?:function|const|var|let|class)\\s+([A-Z]\\w*)/g;
        var defMatch;
        while ((defMatch = defRegex.exec(transpiledCode)) !== null) {
          if (definedNames.indexOf(defMatch[1]) === -1) {
            definedNames.push(defMatch[1]);
          }
        }
        if (definedNames.length === 0) {
          definedNames = ['App', 'Main', 'Component', 'Home', 'Dashboard', 'Counter', 'Demo', 'Page'];
        }

        var returnStatement = definedNames.map(function(name) {
          return 'typeof ' + name + ' !== "undefined" ? ' + name + ' : ';
        }).join('') + 'null';

        var execFn = new Function(
          'React', 'useState', 'useEffect', 'useRef', 'useCallback', 'useMemo', 'useContext', 'useReducer', 'Fragment', 'createContext',
          transpiledCode + '; return ' + returnStatement + ';'
        );

        var MainComponent = execFn(React, useState, useEffect, useRef, useCallback, useMemo, useContext, useReducer, Fragment, createContext);

        if (MainComponent && typeof MainComponent === 'function') {
          if (!__reactRoot) {
            __reactRoot = ReactDOM.createRoot(document.getElementById('root'));
          }
          __reactRoot.render(
            React.createElement(ErrorBoundary, null, React.createElement(MainComponent))
          );
          // Post-render diagnostic: SIN overwrite del root para no romper el
          // React commit en progreso (React 18 concurrent mode puede demorar
          // varios segundos). Solo notificamos al parent via postMessage
          // y dejamos que React/ErrorBoundary muestren el contenido real.
          // Threshold a 5000ms para dar tiempo a Babel transform + React commit
          // + Recharts measurement.
          setTimeout(function() {
            var r = document.getElementById('root');
            if (!r) return;
            if (r.children.length === 0) {
              window.parent.postMessage({ type: 'RENDER_ERROR', error: 'Empty render: no DOM children after 5s' }, '*');
            }
          }, 5000);
          window.parent.postMessage({ type: 'RENDER_SUCCESS' }, '*');
          return;
        }

        // Fallback: intentar con cualquier función
        var funcMatch = userCode.match(/(?:function|const)\\s+(\\w+)/);
        if (funcMatch && funcMatch[1]) {
          var altExecFn = new Function(
            'React', 'useState', 'useEffect', 'useRef', 'useCallback', 'useMemo', 'useContext', 'useReducer', 'Fragment', 'createContext',
            transpiledCode + '; return typeof ' + funcMatch[1] + ' !== "undefined" ? ' + funcMatch[1] + ' : null;'
          );
          var AltComponent = altExecFn(React, useState, useEffect, useRef, useCallback, useMemo, useContext, useReducer, Fragment, createContext);
          if (AltComponent && typeof AltComponent === 'function') {
            if (!__reactRoot) {
              __reactRoot = ReactDOM.createRoot(document.getElementById('root'));
            }
            __reactRoot.render(
              React.createElement(ErrorBoundary, null, React.createElement(AltComponent))
            );
            window.parent.postMessage({ type: 'RENDER_SUCCESS' }, '*');
            return;
          }
        }

        // Fallback: Wrap raw JSX
        try {
          var wrappedCode = 'function __WrappedApp__() { return (' + userCode + '); }';
          var wrappedTranspiled = Babel.transform(wrappedCode, { presets: ['react'], filename: 'wrapped.jsx' }).code;
          var wrappedFn = new Function(
            'React', 'useState', 'useEffect', 'useRef', 'useCallback', 'useMemo', 'useContext', 'useReducer', 'Fragment', 'createContext',
            wrappedTranspiled + '; return __WrappedApp__;'
          );
          var WrappedComponent = wrappedFn(React, useState, useEffect, useRef, useCallback, useMemo, useContext, useReducer, Fragment, createContext);
          if (WrappedComponent && typeof WrappedComponent === 'function') {
            if (!__reactRoot) {
              __reactRoot = ReactDOM.createRoot(document.getElementById('root'));
            }
            __reactRoot.render(
              React.createElement(ErrorBoundary, null, React.createElement(WrappedComponent))
            );
            window.parent.postMessage({ type: 'RENDER_SUCCESS' }, '*');
            return;
          }
        } catch(wrapErr) {
          // surface this error too instead of silent fail
          document.getElementById('root').innerHTML = '<div class="error-display"><strong>Wrap fallback error:</strong>\\n\\n' + wrapErr.message + '</div>';
          window.parent.postMessage({ type: 'RENDER_ERROR', error: 'Wrap: ' + wrapErr.message }, '*');
          return;
        }

        document.getElementById('root').innerHTML = '<div class="error-display"><strong>No se encontró un componente React válido.</strong></div>';
        window.parent.postMessage({ type: 'RENDER_ERROR', error: 'No component found' }, '*');
      } catch (err) {
        console.error('Error:', err);
        document.getElementById('root').innerHTML = '<div class="error-display"><strong>Error de compilación:</strong>\\n\\n' + err.message + '</div>';
        window.parent.postMessage({ type: 'RENDER_ERROR', error: err.message }, '*');
      }
    }

    // Listener de mensajes del padre — canal principal de actualización
    window.addEventListener('message', function(event) {
      if (event.data && event.data.type === 'RENDER_CODE') {
        if (__ready) {
          renderCode(event.data.code);
        } else {
          // Si las libs aún no cargan, esperar
          waitForLibraries(function() {
            __ready = true;
            renderCode(event.data.code);
          });
        }
      }
    });

    // Inicialización: esperar libs y notificar que estamos listos
    waitForLibraries(function() {
      __ready = true;
      window.parent.postMessage({ type: 'IFRAME_READY' }, '*');
    });
  <\/script>
</body>
</html>`;
  }, []);

  // Escuchar mensajes del iframe (ready, success, error)
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data) return;
      if (event.data.type === 'IFRAME_READY') {
        iframeReady.current = true;
        // Si hay código pendiente, enviarlo ahora
        if (pendingCode.current) {
          sendCodeToIframe(pendingCode.current);
          pendingCode.current = null;
        }
      } else if (event.data.type === 'RENDER_SUCCESS') {
        setIsLoading(false);
        setError(null);
      } else if (event.data.type === 'RENDER_ERROR') {
        setIsLoading(false);
        setError(event.data.error);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Enviar código al iframe via postMessage (sin recargar)
  const sendCodeToIframe = (codeToSend) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { type: 'RENDER_CODE', code: codeToSend },
        '*'
      );
      lastSentCode.current = codeToSend;
    }
  };

  // Cuando cambia el código, enviarlo al iframe sin reiniciarlo
  useEffect(() => {
    if (!cleanCode) return;
    if (cleanCode === lastSentCode.current) return;

    // 🟥 CUADRO NEGRO — DETECTOR PRE-RENDER. Si el código generado es un
    // proyecto NO ejecutable en web (React Native, backend, CLI, sin UI),
    // activamos el Modo Alternativo SIN intentar renderizar. Usamos el
    // mismo canal `error` que dispara el panel post-render, con un mensaje
    // marcador `__NON_EXECUTABLE__:<categoria>` que `detectNonExecutableError`
    // reconoce como caso valido (vease patrones al inicio del archivo).
    const preCategory = detectNonExecutableCode(cleanCode);
    if (preCategory) {
      setIsLoading(false);
      setError(`__NON_EXECUTABLE__:${preCategory}`);
      lastSentCode.current = cleanCode;
      return;
    }

    setIsLoading(true);
    setError(null);

    if (iframeReady.current) {
      sendCodeToIframe(cleanCode);
    } else {
      pendingCode.current = cleanCode;
    }
  }, [cleanCode]);

  const handleIframeLoad = () => {
    // El iframe se cargó. Por defecto el iframe DEBE enviar IFRAME_READY
    // por postMessage cuando arranca su listener. Pero hay un caso donde
    // eso no ocurre: cuando React mueve el iframe de un parent a otro
    // (toggle fullscreen). En ese caso el browser RECARGA el iframe
    // pero el ref `iframeReady.current` ya estaba en true por el primer
    // mount, así que `useEffect` nunca le re-envía el código → spinner
    // eterno hasta hacer click en Refrescar.
    //
    // Fix: en cada `load` consideramos el iframe potencialmente RE-montado.
    //  • Reseteamos iframeReady para forzar el ciclo IFRAME_READY de nuevo.
    //  • Tras 1.5s, si no llegó IFRAME_READY, intentamos enviar el código
    //    igualmente (suficiente para iframes con srcDoc inline que ya tienen
    //    listener instalado).
    iframeReady.current = false;
    pendingCode.current = cleanCode || null;
    const timer = setTimeout(() => {
      if (!iframeReady.current && (pendingCode.current || cleanCode)) {
        // Suponer readiness e intentar enviar — el iframe puede que ya
        // tenga su listener pero el postMessage IFRAME_READY se perdió.
        iframeReady.current = true;
        sendCodeToIframe(pendingCode.current || cleanCode);
        pendingCode.current = null;
      }
    }, 1500);
    return () => clearTimeout(timer);
  };

  // 🟥 FIX ESTRUCTURAL DEFINITIVO — eliminado el workaround que reseteaba
  // iframeReady al togglear fullscreen. Ahora el iframe vive UNA sola vez
  // en el árbol (Fragment + backdrop con display:none) por lo que NO se
  // desmonta ni se recarga al cambiar de tamaño. El srcDoc nunca se pierde.

  const handleReload = () => {
    // Re-enviar el código actual sin recargar el iframe
    if (cleanCode) {
      setIsLoading(true);
      sendCodeToIframe(cleanCode);
    }
  };

  const previewContent = (
    <div className={`flex flex-col overflow-hidden border-0 ${isFullscreen ? 'fixed inset-0 sm:inset-4 z-50 sm:rounded-xl' : 'h-full'}`} 
         style={{ backgroundColor: COLORS.panel, borderColor: COLORS.border }}>
      <div className="flex items-center justify-between px-2 sm:px-3 py-1.5 border-b flex-shrink-0" style={{ borderColor: COLORS.border }}>
        <div className="flex items-center gap-2 min-w-0">
          <Monitor className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
          <span className="text-xs font-medium text-white truncate">
            {title || (language === 'es' ? 'Vista Previa React' : 'React Preview')}
          </span>
          {/* 🟪 P24.9-CODE · Tab "Códigos/Code" INMEDIATAMENTE a la derecha de "Vista Previa React" */}
          <button
            onClick={() => setShowCodePanel((v) => !v)}
            className={`ml-1 px-2 py-0.5 rounded text-[11px] font-medium transition-colors flex items-center gap-1 shrink-0 ${
              showCodePanel
                ? 'bg-emerald-500 text-white'
                : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
            }`}
            data-testid="workspace-code-tab"
            title={language === 'es' ? 'Ver archivos generados' : 'View generated files'}
          >
            <FileCode2 className="w-3 h-3" />
            <span>{language === 'es' ? 'Códigos' : 'Code'}</span>
          </button>
          {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-400" />}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleReload}
            className="p-1 rounded hover:bg-zinc-700 transition-colors"
            title={language === 'es' ? 'Recargar' : 'Reload'}
            data-testid="preview-reload-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 text-zinc-400" />
          </button>
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-1 rounded hover:bg-zinc-700 transition-colors"
            data-testid="preview-fullscreen-btn"
          >
            {isFullscreen ? <Minimize2 className="w-3.5 h-3.5 text-zinc-400" /> : <Maximize2 className="w-3.5 h-3.5 text-zinc-400" />}
          </button>
        </div>
      </div>
      
      <div className="relative flex-1 min-h-0" style={{ minHeight: '200px' }}>
        {error && !isLoading ? (
          detectNonExecutableError(error) ? (
            // 🟥 CUADRO NEGRO — MODO ALTERNATIVO DE PREVIEW.
            // El proyecto generado NO es ejecutable en el sandbox web
            // (React Native, cámara, OCR, backend sin UI, etc.). En vez
            // del error técnico rojo, mostramos un panel comunicativo que
            // tranquiliza al usuario y le guía a descargar el código.
            <div
              className="flex flex-col items-center justify-center h-full px-6 py-8 overflow-y-auto"
              data-testid="workspace-alt-mode"
            >
              {/* CUADRO NEGRO — Mockup ilustrativo del Panel de Descarga.
                  Imagen vectorial: pantalla de dispositivo con líneas de
                  código + check verde de "completado". Máximo 260×200 px
                  (cumple el límite de 300px). Inline SVG → sin asset
                  externo, escala sin pérdida, sin requests de red. */}
              <svg
                viewBox="0 0 260 200"
                width="100%"
                style={{ maxWidth: '260px', height: 'auto', marginBottom: '24px' }}
                aria-hidden="true"
                data-testid="workspace-alt-mockup"
              >
                <defs>
                  <linearGradient id="altScreenGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1e293b" />
                    <stop offset="100%" stopColor="#0f172a" />
                  </linearGradient>
                  <linearGradient id="altGlowGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
                  </linearGradient>
                </defs>

                {/* Glow detrás del check */}
                <circle cx="210" cy="44" r="44" fill="url(#altGlowGrad)" />

                {/* Pantalla / device frame */}
                <rect x="20" y="20" width="220" height="150" rx="14" ry="14"
                      fill="url(#altScreenGrad)" stroke="#334155" strokeWidth="1.5" />

                {/* Barra superior (3 dots tipo macOS) */}
                <circle cx="36" cy="36" r="3.5" fill="#475569" />
                <circle cx="48" cy="36" r="3.5" fill="#475569" />
                <circle cx="60" cy="36" r="3.5" fill="#475569" />
                <line x1="20" y1="50" x2="240" y2="50" stroke="#334155" strokeWidth="1" />

                {/* Líneas de "código" minimalistas. Dos líneas centrales
                    tienen clases `code-line-1` / `code-line-2` para
                    fade-in escalonado (anim. CUADRO NEGRO). */}
                <rect x="36" y="64" width="32" height="6" rx="2" fill="#a78bfa" opacity="0.85" />
                <rect x="72" y="64" width="62" height="6" rx="2" fill="#60a5fa" opacity="0.7" />

                <rect className="code-line-1" x="50" y="80" width="22" height="6" rx="2" fill="#f472b6" opacity="0.8" />
                <rect className="code-line-1" x="76" y="80" width="80" height="6" rx="2" fill="#cbd5e1" opacity="0.55" />

                <rect className="code-line-2" x="50" y="96" width="40" height="6" rx="2" fill="#34d399" opacity="0.85" />
                <rect className="code-line-2" x="94" y="96" width="48" height="6" rx="2" fill="#cbd5e1" opacity="0.55" />

                <rect x="36" y="112" width="28" height="6" rx="2" fill="#a78bfa" opacity="0.85" />
                <rect x="68" y="112" width="92" height="6" rx="2" fill="#cbd5e1" opacity="0.55" />

                <rect x="50" y="128" width="36" height="6" rx="2" fill="#fbbf24" opacity="0.8" />
                <rect x="90" y="128" width="56" height="6" rx="2" fill="#cbd5e1" opacity="0.55" />

                <rect x="36" y="144" width="20" height="6" rx="2" fill="#a78bfa" opacity="0.85" />

                {/* Check verde "completado" — con pulse infinito sutil. */}
                <circle className="alt-check" cx="210" cy="44" r="22"
                        fill={COLORS.success} stroke="#0f172a" strokeWidth="3" />
                <path className="alt-check" d="M200 44 L208 52 L222 38"
                      fill="none" stroke="white" strokeWidth="3.5"
                      strokeLinecap="round" strokeLinejoin="round" />
              </svg>

              <h3 className="text-white text-lg sm:text-xl font-semibold text-center mb-3 max-w-md">
                {language === 'es'
                  ? 'Tu trabajo está hecho y completado como código'
                  : 'Your work is done and completed as code'}
              </h3>
              {(() => {
                // CUADRO NEGRO — Subtitulo bilingue por categoria.
                // 1) Detector de idioma: prioridad al `language` del context
                //    (refleja la eleccion manual del usuario via toggle ES/EN).
                //    Si no esta disponible -> `navigator.language` (fallback
                //    del cuadro). Si tampoco -> 'en'.
                // 2) Si la categoria es null -> NO renderizar subtitulo
                //    (regla explicita del cuadro).
                const cat = extractAltCategory(error);
                if (!cat) return null;
                let lang = language;
                if (lang !== 'es' && lang !== 'en') {
                  const navLang = (typeof navigator !== 'undefined' && navigator.language) || 'en';
                  lang = navLang.toLowerCase().startsWith('es') ? 'es' : 'en';
                }
                const specific = ALT_MESSAGES[lang] && ALT_MESSAGES[lang][cat];
                if (!specific) return null;
                return (
                  <p
                    className="text-zinc-400 text-sm text-center max-w-md mb-6 leading-relaxed"
                    data-testid="workspace-alt-subtitle"
                    data-category={cat}
                  >
                    {specific}
                  </p>
                );
              })()}
              <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
                {typeof onDownloadProject === 'function' && (
                  <button
                    type="button"
                    onClick={onDownloadProject}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-medium transition-colors"
                    style={{ backgroundColor: COLORS.success }}
                    data-testid="workspace-alt-download-btn"
                  >
                    <Download className="w-4 h-4" />
                    {language === 'es' ? 'Descargar Proyecto' : 'Download Project'}
                  </button>
                )}
                {typeof onShowFiles === 'function' && (
                  <button
                    type="button"
                    onClick={onShowFiles}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-medium border transition-colors hover:bg-zinc-800"
                    style={{ borderColor: COLORS.border }}
                    data-testid="workspace-alt-files-btn"
                  >
                    <FolderOpen className="w-4 h-4" />
                    {language === 'es' ? 'Ver Archivos Generados' : 'View Generated Files'}
                  </button>
                )}
                {Array.isArray(projectFiles) && projectFiles.length > 0 && (
                  <SaveToGitHubButton files={projectFiles} defaultRepoName="damyxia-project" />
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full bg-red-900/20">
              <div className="text-center">
                <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            </div>
          )
        ) : null}
        <iframe
          ref={iframeRef}
          srcDoc={stableTemplate}
          className="w-full h-full bg-zinc-900"
          // 🟥 P24.2 · Bug reportado por owner: el sandbox="allow-scripts"
          // sin allow-same-origin bloquea localStorage, sessionStorage,
          // cookies y todas las APIs de mismo origen. Provocaba errores
          // "Failed to read the 'localStorage' property from 'Window':
          // The document is sandboxed and lacks the 'allow-same-origin' flag."
          // en cualquier proyecto que use localStorage. Añadir
          // allow-same-origin junto con allow-scripts es un warning conocido
          // pero es el patrón estándar cuando el iframe necesita ambos.
          // (Coherente con UnifiedCodePreview / AppViewer / PublicModulePage
          // / UiBuilderPage que ya lo tenían).
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          onLoad={handleIframeLoad}
          title={language === 'es' ? 'Vista Previa React' : 'React Preview'}
          data-testid="react-preview-iframe"
          data-ph-no-capture="true"
          style={{ display: error && !isLoading ? 'none' : 'block' }}
        />
        
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/80">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-400 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">
                {language === 'es' ? 'Compilando componente...' : 'Compiling component...'}
              </p>
            </div>
          </div>
        )}

        {/* 🟪 Panel Códigos · visor de archivos del proyecto abierto.
            NO depende del chat. Recibe projectFiles directamente. */}
        {showCodePanel && (
          <div
            className="absolute inset-0 z-30"
            data-testid="code-panel-overlay"
          >
            <CodePanel
              projectFiles={codePanelFiles}
              projectName={title}
              language={language}
              onClose={() => setShowCodePanel(false)}
            />
          </div>
        )}
      </div>
    </div>
  );

  // 🟥 FIX ESTRUCTURAL DEFINITIVO — el iframe vive UNA sola vez en el
  // árbol. Siempre devolvemos el mismo Fragment con:
  //   1. backdrop (display:none cuando NO está fullscreen)
  //   2. contenedor del iframe (cambia solo su clase según isFullscreen)
  // Esto garantiza que el iframe NUNCA se desmonta al togglear tamaño,
  // por lo que el srcDoc no se pierde y no hay flash en blanco.
  return (
    <>
      <div
        className="fixed inset-0 bg-black/80 z-40"
        style={{ display: isFullscreen ? 'block' : 'none' }}
        onClick={() => setIsFullscreen(false)}
        aria-hidden={!isFullscreen}
        data-testid="preview-fullscreen-backdrop"
      />
      {previewContent}
    </>
  );
};

// =====================================================
// WORKSPACE UNIFICADO
// =====================================================
export const CodeWorkspace = ({ codeBlocks, executeActions, language }) => {
  const isSpanish = language === 'es';
  
  // Filtrar bloques de código ejecutables
  const executableBlocks = useMemo(() => {
    const blocks = [];
    
    // Agregar bloques de código React/JSX
    codeBlocks.forEach(block => {
      if (block.autoExecute || block.isReactComponent) {
        blocks.push({
          type: 'react',
          code: block.code,
          language: block.language,
          id: block.id
        });
      } else if (['javascript', 'js'].includes(block.language?.toLowerCase())) {
        blocks.push({
          type: 'javascript',
          code: block.code,
          language: block.language,
          id: block.id
        });
      }
    });
    
    // Agregar acciones de ejecución
    executeActions.forEach(action => {
      if (action.type === 'preview' || action.source === 'code-block') {
        if (!blocks.find(b => b.code === action.code)) {
          blocks.push({
            type: 'react',
            code: action.code,
            language: action.language,
            id: action.id
          });
        }
      } else if (action.type === 'execute') {
        blocks.push({
          type: 'javascript',
          code: action.code,
          language: action.language,
          id: action.id
        });
      }
    });
    
    return blocks;
  }, [codeBlocks, executeActions]);

  if (executableBlocks.length === 0) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-4 h-4 text-amber-400" />
        <span className="text-sm font-medium text-white">
          {isSpanish ? 'Entorno de Ejecución' : 'Execution Environment'}
        </span>
        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
          {executableBlocks.length} {isSpanish ? 'bloques' : 'blocks'}
        </span>
      </div>
      
      {executableBlocks.map((block) => (
        <div key={block.id}>
          {block.type === 'react' ? (
            <ReactPreview 
              code={block.code} 
              autoRun={true}
              title={isSpanish ? 'Componente React' : 'React Component'}
            />
          ) : (
            <JavaScriptRunner 
              code={block.code} 
              autoRun={true}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default {
  JavaScriptRunner,
  ReactPreview,
  CodeWorkspace
};
